import { INVITATION_VALID_DAYS, STORED_FILE_TYPES_TEXT, type HelpTopic } from '@kanos/domain';

/**
 * The help articles of the firm app (after Corly's help): each describes one area as it works
 * today, in the words of the app. `paths` names the pages an article belongs to - the help menu
 * offers it there; the longest matching path wins. Text for people, so German; when an area
 * changes, its article changes with it.
 */
export interface HelpArticle {
  /** Its topic - the questions of the FAQ belong to it as well. */
  slug: HelpTopic;
  title: string;
  summary: string;
  paths: readonly string[];
  sections: readonly { heading: string; paragraphs: readonly string[] }[];
}

export const HELP_ARTICLES: readonly HelpArticle[] = [
  {
    slug: 'getting-started',
    title: 'Erste Schritte',
    summary: 'Wie Kanos aufgebaut ist und womit Sie anfangen.',
    paths: ['/'],
    sections: [
      {
        heading: 'Heute',
        paragraphs: [
          'Die Startseite zeigt, was gerade auf die Kanzlei wartet: Angaben, die Kanos noch von Ihnen braucht, und Hinweise, die Kanos für Sie hat – etwa wenn ein Webhook beendet wurde. Daneben sehen Sie, welche Pflichten für die Kanzlei gelten.',
          'Die Glocke oben rechts zeigt dieselben offenen Aufgaben von jeder Seite aus.',
        ],
      },
      {
        heading: 'Der erste Weg',
        paragraphs: [
          'Tragen Sie unter Kanzlei › Basisdaten ein, was Kanos über die Kanzlei wissen muss, und unter Berufsträger, wer in der Kanzlei zugelassen ist. Daraus ermittelt Kanos, welche Pflichten für die Kanzlei gelten.',
          'Admins schließen unter Einstellungen › Abrechnung & Recht › Rechtsdokumente den Vertrag zur Auftragsverarbeitung (AVV) mit Kanos ab.',
        ],
      },
      {
        heading: 'Schnell ans Ziel',
        paragraphs: [
          'Mit „Suchen …“ oben oder Strg K (auf dem Mac ⌘K) springen Sie zu jeder Seite, indem Sie ihren Namen tippen. Alle Tastenkürzel zeigt Strg / (⌘/).',
        ],
      },
      {
        heading: 'Was ist neu?',
        paragraphs: [
          'Unter Hilfe › Neuigkeiten schreibt das Kanos-Team, was sich in Kanos ändert. Ein Punkt am Fragezeichen zeigt, dass es seit Ihrem letzten Blick etwas Neues gibt.',
          'Unten auf jeder Seite steht die Version von Kanos. Nennen Sie sie, wenn Sie sich an den Support wenden.',
        ],
      },
    ],
  },
  {
    slug: 'obligations',
    title: 'Pflichten der Kanzlei',
    summary: 'Woher die Pflichten kommen und was ihre Stände bedeuten.',
    paths: ['/obligations'],
    sections: [
      {
        heading: 'Woher die Pflichten kommen',
        paragraphs: [
          'Kanos ermittelt die Pflichten der Kanzlei aus ihren Basisdaten und Berufsträgern – nach Regeln, die Fachleute geprüft und freigegeben haben. Kanos hält jedes Mal fest, welche Regel in welcher Fassung mit welchen Angaben gerechnet hat.',
          'Ändern sich die Angaben, ermittelt Kanos die Pflichten von selbst neu.',
        ],
      },
      {
        heading: 'Die Stände',
        paragraphs: [
          '„ermittelt“: Eine Regel hat entschieden, ob die Pflicht für die Kanzlei gilt.',
          '„keine Regel trifft auf Ihre Kanzlei zu“: Kanos hat Regeln zu dieser Pflicht, aber keine passt zu den Angaben der Kanzlei. Das heißt nicht sicher, dass die Pflicht nicht gilt – prüfen Sie es im Zweifel selbst.',
          '„noch nicht von Kanos geprüft“: Zu dieser Pflicht gibt es noch keine freigegebene Regel. Kanos sagt dazu bewusst nichts; ob sie gilt, beurteilt die Kanzlei.',
          '„Regel nicht auswertbar“: Eine Regel ließ sich technisch nicht auswerten. Das ist nie ein „gilt nicht“; Kanos legt dafür eine Aufgabe an.',
        ],
      },
      {
        heading: 'Fragen klären',
        paragraphs: [
          'Fehlt Kanos für eine Regel eine Angabe, entsteht eine Frage „zu klären“. Sie verschwindet, sobald die Angabe in den Basisdaten steht – „unbekannt“ ist dabei eine Antwort, kein leeres Feld.',
        ],
      },
    ],
  },
  {
    slug: 'base-data',
    title: 'Basisdaten, Berufsträger und Standorte',
    summary: 'Angaben der Kanzlei mit Herkunft, Historie und Nachweisen.',
    paths: ['/firm/base-data', '/firm/professionals', '/firm/locations'],
    sections: [
      {
        heading: 'Angaben mit Herkunft',
        paragraphs: [
          'Jede Angabe wird mit ihrer Herkunft und ihrem Prüfstatus gespeichert. Eine neue Angabe gilt ab jetzt; die frühere bleibt in der Historie erhalten.',
          '„Unbekannt“ ist eine Antwort: Wer etwas noch nicht weiß, sagt es so – Kanos wertet das nie als „nein“.',
        ],
      },
      {
        heading: 'Nachweise',
        paragraphs: [
          `Zu einer Angabe und zu einer Berufszulassung können Sie Nachweise ablegen: ${STORED_FILE_TYPES_TEXT}. Kanos prüft jede Datei auf Schadsoftware und speichert sie verschlüsselt.`,
        ],
      },
      {
        heading: 'Wer ändern darf',
        paragraphs: [
          'Alle aus der Kanzlei sehen die Basisdaten; ändern und ergänzen können Admins.',
        ],
      },
    ],
  },
  {
    slug: 'team',
    title: 'Team und Rechte',
    summary: 'Wer zur Kanzlei gehört, wer sie verwaltet und welche Rolle wer hat.',
    paths: ['/firm/team'],
    sections: [
      {
        heading: 'Wer im Team steht',
        paragraphs: [
          'Im Team steht, wer zur Kanzlei gehört und sich schon einmal bei Kanos angemeldet hat. Wer zur Kanzlei gehört, legt das Benutzerverzeichnis der Kanzlei fest.',
        ],
      },
      {
        heading: 'Admins',
        paragraphs: [
          'Admins verwalten Basisdaten, Zugänge, Webhooks, die Sandbox, den Datenschutz und das Team. Jede Änderung der Rechte steht im Audit-Log. Die Kanzlei bleibt nie ohne Admin.',
          'Für folgenreiche Schritte – etwa einen Vertrag abschließen oder Daten herausgeben – fragt Kanos noch einmal nach Ihrem zweiten Faktor.',
        ],
      },
      {
        heading: 'Rollen',
        paragraphs: [
          'Unter „Rollen“ legen Admins fest, wer was sieht. Eine Person kann mehrere Rollen haben; ohne Rolle arbeitet sie wie bisher.',
          'Berufsträger bewerten in einer Mandatsaufnahme das Geldwäscherisiko. Die Sachbearbeitung bearbeitet Aufnahmen, erfasst Ausweisdaten und sieht Risikobewertungen.',
          'Das Sekretariat nimmt Anfragen an und beginnt Aufnahmen. Ausweisdaten, Risikobewertungen und die GwG-Aufzeichnung sieht es nicht – außer die Person hat auch die Sachbearbeitung.',
        ],
      },
      {
        heading: 'Mandatsteams',
        paragraphs: [
          'Admins legen Teams an und nehmen Personen auf. Bei den Verantwortlichen einer Aufnahme wählen Sie ihr Team.',
          'Mit „Teams trennen“ sieht eine Aufnahme mit Team nur ihr Team, ihre Verantwortlichen, die Admins und die Berufsträger. Aufnahmen ohne Team sehen alle der Kanzlei.',
        ],
      },
    ],
  },
  {
    slug: 'appearance-and-domains',
    title: 'Erscheinungsbild und Portal-Domains',
    summary: 'Kanzleifarbe, Logo und eigene Domains für das Mandantenportal.',
    paths: ['/settings/appearance', '/settings/portal-domains'],
    sections: [
      {
        heading: 'Kanzleifarbe',
        paragraphs: [
          'Unter Erscheinungsbild wählen Admins die Farbe der Kanzlei. Sie gilt für alle aus der Kanzlei und im Mandantenportal unter einer eigenen Domain der Kanzlei. Damit Text in der Farbe gut lesbar bleibt, passt Kanos ihre Helligkeit an.',
        ],
      },
      {
        heading: 'Logo',
        paragraphs: [
          'Ebenfalls unter Erscheinungsbild laden Admins das Logo der Kanzlei hoch und schneiden es auf den Rahmen zu, in dem es erscheint. Kanos prüft jedes Bild und speichert es neu. Das Logo erscheint im Mandantenportal und, wenn unter E-Mail › Design gewählt, im Kopf der Mails – dort über die bestätigte Portal-Domain der Kanzlei.',
        ],
      },
      {
        heading: 'Eigene Domain',
        paragraphs: [
          'Unter einer eigenen Domain zeigt das Mandantenportal Namen und Farbe der Kanzlei. Tragen Sie die Domain ein und setzen Sie die angezeigten DNS-Einträge. Kanos prüft den Nachweis von selbst und stellt danach das Zertifikat aus.',
        ],
      },
    ],
  },
  {
    slug: 'inquiries',
    title: 'Anfragen über das Mandantenportal',
    summary: 'Wie Interessenten die Kanzlei erreichen und was die Kanzlei dafür hinterlegt.',
    paths: ['/inquiries', '/settings/portal-legal'],
    sections: [
      {
        heading: 'Impressum und Datenschutz',
        paragraphs: [
          'Im Mandantenportal ist die Kanzlei verantwortlich. Deshalb hinterlegen Admins unter Einstellungen › Auftritt › Impressum und Datenschutz die Adressen dieser Seiten auf der Website der Kanzlei. Erst mit beiden nimmt das Portal Anfragen an.',
        ],
      },
      {
        heading: 'Eine Anfrage geht ein',
        paragraphs: [
          'Interessenten nennen im Portal Art, Name, E-Mail-Adresse und ihr Anliegen und bestätigen die Datenschutzhinweise der Kanzlei. Die Anfrage erscheint unter Anfragen; die Kanzlei erhält einen Hinweis ohne persönliche Angaben.',
          'Wer geantwortet hat, markiert die Anfrage als erledigt. Das steht mit Zeitpunkt und Person im Protokoll.',
        ],
      },
      {
        heading: 'Aufbewahrung',
        paragraphs: [
          'Für Anfragen ohne Mandat gilt keine Aufbewahrungspflicht. Kanos löscht sie sechs Monate nach der Erledigung, unbeantwortete sechs Monate nach dem Eingang. Admins können die Frist unter Einstellungen › Mandatsannahme › Anfragen zwischen 1 und 24 Monaten wählen.',
        ],
      },
      {
        heading: 'Schutz vor Spam',
        paragraphs: [
          'Das Portal nimmt von einer Adresse nur wenige Anfragen in kurzer Zeit an und erkennt automatisch ausgefüllte Formulare. Ein Captcha oder ein Dienst Dritter ist dafür nicht nötig.',
        ],
      },
    ],
  },
  {
    slug: 'intakes',
    title: 'Mandatsaufnahme und Geldwäscheprävention',
    summary: 'Wirtschaftlich Berechtigte, Identifizierung und Risikobewertung nach dem GwG.',
    paths: ['/intakes'],
    sections: [
      {
        heading: 'Eine Aufnahme beginnt',
        paragraphs: [
          'Eine Mandatsaufnahme beginnt auf zwei Wegen. Kam die Person über das Mandantenportal, öffnen Sie unter Mandanten › Anfragen ihre Anfrage und wählen „Mandatsaufnahme starten“; die Anfrage gilt damit als erledigt. Für alle anderen wählen Sie „Neue Aufnahme“ unter Mandanten oder auf der Startseite und nennen Art, Name und E-Mail-Adresse.',
          'Jeder Mandant ist ein eigener Datensatz mit allen seinen Aufnahmen. Eine weitere Aufnahme – etwa zur Aktualisierung – beginnen Sie in seiner Übersicht unter „Verwalten“ › „Neue Aufnahme beginnen“.',
          'Solange die Aufnahme offen ist, erfasst die Kanzlei, was sie feststellt. Ist sie abgeschlossen oder das Mandat abgelehnt, bleibt alles erhalten und lässt sich nicht mehr ändern.',
        ],
      },
      {
        heading: 'Einladung ins Portal',
        paragraphs: [
          `Unter „Einladung ins Portal“ schicken Sie der Person der Aufnahme einen Link an ihre E-Mail-Adresse. Er führt auf die Portal-Domain der Kanzlei, gilt ${String(INVITATION_VALID_DAYS)} Tage und nur einmal. Dort legt die Person ein Konto mit zweitem Faktor an und nimmt die Einladung an.`,
          'Eine neue Einladung ersetzt die offene. Ohne bestätigte Portal-Domain (Einstellungen › Portal-Domains) ist keine Einladung möglich.',
          'Kommen keine Angaben, erinnert Kanos die Person per E-Mail – vor der Annahme mit einem neuen Link – und gibt danach der Kanzlei einen Hinweis. Wann und wie oft, legen Admins unter Einstellungen › Mandatsannahme › Erinnerungen & Fristen fest: Jede Erinnerung lässt sich ausschalten, ändern, kopieren oder entfernen, und eigene kommen hinzu. Standard sind Erinnerungen nach 3 und 7 Tagen und der Hinweis nach 11 Tagen.',
          'Im Portal macht die Person ihre Angaben zum Vertragspartner und nennt wirtschaftlich Berechtigte. Sendet sie die Angaben, erhält die Kanzlei einen Hinweis; unter „Angaben zum Vertragspartner“ halten Sie fest, wie weit Sie die Angaben geprüft haben.',
        ],
      },
      {
        heading: 'Wirtschaftlich Berechtigte',
        paragraphs: [
          'Zu jeder Person halten Sie fest, warum sie wirtschaftlich berechtigt ist, woher die Angabe stammt – von der Mandantschaft, aus eigener Feststellung oder aus einem Register – und wie weit sie geprüft ist. Was Sie ändern oder entfernen, bleibt in der Historie.',
        ],
      },
      {
        heading: 'Identifizierung',
        paragraphs: [
          'Identifiziert wird vor Ort mit dem Ausweis im Original – oder online: Mit „Identifizieren“ › „Online per Link“ bekommt die Person einen Link per E-Mail und weist sich bei einem zugelassenen Dienst mit der Online-Ausweisfunktion oder per Video aus. Online geht das, sobald die Kanzlei einen Identifizierungsdienst angebunden hat.',
          'Kanos speichert die Ausweisdaten verschlüsselt mit einem eigenen Schlüssel; Bilder des Ausweises liegen getrennt davon.',
          'Wer Ausweisdaten anzeigt oder ein Bild herunterlädt, nennt den Zweck und bestätigt sich mit dem zweiten Faktor. Jeder Zugriff steht mit Person, Zeit und Zweck im Protokoll.',
        ],
      },
      {
        heading: 'Risikobewertung',
        paragraphs: [
          'Das Risiko bewertet ein Berufsträger der Kanzlei – gering, normal oder hoch – und begründet es. Die neueste Bewertung gilt, frühere bleiben erhalten.',
        ],
      },
      {
        heading: 'Abschluss und was Kanos selbst erledigt',
        paragraphs: [
          'Abschließen lässt sich eine Aufnahme erst, wenn eine Risikobewertung vorliegt und jeder Treffer aus dem Abgleich entschieden ist. Hat ein Abgleich noch kein Ergebnis, schließen Sie nur mit einer schriftlichen Begründung ab.',
          'Unter Einstellungen › Mandatsannahme › Automatisierte GwG-Prüfung legen Admins fest, was Kanos selbst erledigt – etwa sofort ins Portal einladen, das Handelsregister abfragen oder die Aufnahme abschließen, sobald alles entschieden ist. Die Risikostufe, jede Entscheidung über einen Treffer und die Identifizierung vor Ort bleiben bei der Kanzlei.',
          'Nach dem Abschluss legt Kanos eine Wiedervorlage für die nächste Aktualisierung an. Ändert sich danach etwas – etwa Rollen oder Stammdaten des Mandanten –, zeigt Kanos es in der Übersicht des Mandanten; ob eine Aktualisierung nötig ist, entscheidet die Kanzlei.',
          'Welche Sorgfaltspflichten das GwG im Einzelfall verlangt und wie hoch das Risiko ist, beurteilt die Kanzlei. Kanos macht dazu keinen Vorschlag.',
        ],
      },
    ],
  },
  {
    slug: 'interfaces',
    title: 'Schnittstellen',
    summary: 'API-Zugänge, Webhooks, Partner-Apps und die Sandbox.',
    paths: [
      '/settings/api-clients',
      '/settings/webhooks',
      '/settings/partner-apps',
      '/settings/sandbox',
    ],
    sections: [
      {
        heading: 'API-Zugänge',
        paragraphs: [
          'Eigene Systeme der Kanzlei arbeiten über die Kanos-API. Jeder Zugang bekommt nur die Rechte, die er braucht. Den Schlüssel sehen Sie beim Anlegen genau einmal.',
        ],
      },
      {
        heading: 'Webhooks',
        paragraphs: [
          'Kanos meldet Änderungen an Systeme der Kanzlei. Jede Meldung nennt nur, was sich geändert hat; die Daten liest das System über seinen API-Zugang. Scheitert ein Webhook dauerhaft, beendet Kanos ihn und gibt Bescheid.',
        ],
      },
      {
        heading: 'Partner-Apps und Sandbox',
        paragraphs: [
          'Partner-Apps sind von Kanos geprüfte Anwendungen, die die Kanzlei verbinden kann. Die Sandbox ist eine Testkanzlei mit Beispieldaten, um Schnittstellen auszuprobieren, ohne echte Daten zu berühren.',
        ],
      },
    ],
  },
  {
    slug: 'privacy',
    title: 'Datenschutz',
    summary: 'Auftragsverarbeitung, Datenexport und Auskunft für betroffene Personen.',
    paths: ['/settings/processing-agreement', '/settings/data-export', '/settings/disclosure'],
    sections: [
      {
        heading: 'Auftragsverarbeitung',
        paragraphs: [
          'Kanos verarbeitet die Daten der Kanzlei in ihrem Auftrag. Admins schließen dafür den Vertrag nach Art. 28 DSGVO ab. Ändert sich der Text, wird die neue Fassung erneut abgeschlossen; die frühere Annahme bleibt als Nachweis.',
        ],
      },
      {
        heading: 'Datenexport',
        paragraphs: [
          'Admins fordern alle Daten der Kanzlei als ZIP-Archiv an: die Datensätze als JSON, die Dateien im Original. Kanos erstellt es im Hintergrund und gibt Bescheid; danach steht es sieben Tage bereit.',
          'Die Ausweisdaten aus Identifizierungen und die Bilder der Ausweise sind ein eigener Teil des Exports. Admins laden ihn gesondert herunter und nennen dafür einen Zweck; jedes Herunterladen steht im Audit-Log.',
        ],
      },
      {
        heading: 'Auskunft',
        paragraphs: [
          'Verlangt eine Person Auskunft über ihre Daten, erstellt Kanos eine Datei mit allem, was die Kanzlei in Kanos über sie gespeichert hat. Jede Auskunft steht im Audit-Log.',
          'Für Interessenten erstellen Admins die Auskunft an der Anfrage. Für die Personen einer Mandatsaufnahme – Vertragspartner, Vertretungsberechtigte, wirtschaftlich Berechtigte – erstellen die Verantwortlichen sie in der Übersicht des Mandanten unter „Verwalten“ › „Datenschutzauskunft“, je Person und nur mit deren eigenen Daten, Ausweisdaten eingeschlossen. Im Mandantenportal lädt jede Person selbst herunter, was sie dort angegeben hat.',
          'Teammitglieder und Berufsträger löschen Admins auf Verlangen unter Einstellungen › Datenschutz › Löschung, Interessenten direkt an der Anfrage.',
          'Daten aus einer Mandatsaufnahme unterliegen Aufbewahrungspflichten (GwG, StBerG). Die Löschung beantragen Admins in der Übersicht des Mandanten unter „Verwalten“ › „Löschen“; Kanos sperrt den Mandanten sofort und löscht erst, wenn die Fristen abgelaufen sind. Zurücknehmen lässt sich der Antrag unter Einstellungen › Datenschutz › Löschung.',
        ],
      },
    ],
  },
  {
    slug: 'billing',
    title: 'Tarife und Abrechnung',
    summary: 'Was kostenlos ist, was ein Tarif kostet und wie die Testphase funktioniert.',
    paths: ['/settings/billing'],
    sections: [
      {
        heading: 'Kostenlose Basis',
        paragraphs: [
          'Basisdaten, Berufsträger, Standorte, Team, Erscheinungsbild, die Pflichtenübersicht und der Datenschutz der Kanzlei bleiben dauerhaft kostenlos.',
        ],
      },
      {
        heading: 'Tarife',
        paragraphs: [
          'Ein Tarif schaltet ein Produkt frei, etwa CHECK-POINT mit dem Mandantenportal. Schnittstellen (API, Webhooks, Sandbox, Partner-Apps) sind in jedem Tarif enthalten.',
          'Ein Tarif kostet einen Grundpreis je Kanzlei und einen gestaffelten Preis je Berufsträger, monatlich oder jährlich. Gezählt werden Personen mit einer gültigen Berufsqualifikation in den Basisdaten. Ändert sich die Zahl, passt Kanos die Abrechnung anteilig an.',
        ],
      },
      {
        heading: 'Testphase',
        paragraphs: [
          'Den ersten Tarif kann die Kanzlei kostenlos testen, ohne Zahlungsmittel. Ist am Ende keins hinterlegt, endet der Tarif von selbst; die Daten bleiben erhalten.',
          'Bezahlt wird über Stripe. Zahlungsweise, Rechnungen und Kündigung verwalten Admins unter Einstellungen › Abrechnung & Recht › Abonnement bei Stripe; Kanos speichert keine Zahlungsdaten.',
        ],
      },
    ],
  },
  {
    slug: 'account',
    title: 'Ihr Konto und die Anmeldung',
    summary: 'Profil, Passkeys, Authenticator-App, Geräte und die automatische Abmeldung.',
    paths: ['/profile'],
    sections: [
      {
        heading: 'Anmeldung & Sicherheit',
        paragraphs: [
          'Unter Profil › Anmeldung & Sicherheit ändern Sie Ihr Passwort, richten Passkeys und eine Authenticator-App ein und sehen, auf welchen Geräten Sie angemeldet sind. Andere Geräte können Sie dort abmelden.',
          'Ihren Namen ändern Sie unter Profil › Persönliche Daten.',
        ],
      },
      {
        heading: 'Automatische Abmeldung',
        paragraphs: [
          'Arbeiten Sie eine Weile nicht in Kanos, meldet Kanos Sie ab – zum Schutz der Daten. Zwei Minuten vorher fragt Kanos „Sind Sie noch da?“. Mit „Angemeldet bleiben“ arbeiten Sie weiter.',
          'Ungespeicherte Eingaben schützt Kanos: Bevor Sie eine Seite mit geänderten Feldern verlassen, fragt Kanos nach.',
        ],
      },
    ],
  },
];

/** The article of a page, by the longest path that matches; the start page only exactly. */
export function articleFor(pathname: string): HelpArticle | null {
  let best: { article: HelpArticle; length: number } | null = null;
  for (const article of HELP_ARTICLES) {
    for (const path of article.paths) {
      const matches =
        path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
      if (matches && path.length > (best?.length ?? -1)) best = { article, length: path.length };
    }
  }
  return best?.article ?? null;
}
